import React, { useMemo, useState } from "react"
import { AtAGlanceContent } from "./AtAGlanceContent"
import { Location } from "./Location"
import { LocationMap } from "./Locations/LocationMap"
import { NewPatientStatus, NewPatientStatusEnum } from "./NewPatientStatus"
import { ProvidedCare } from "./ProvidedCare"
import { SpecialitiesContent } from "../shared/SpecialitiesContent"
import { ClientFocusContent } from "../shared/ClientFocusContent"
import { TreatmentApproachContent } from "../shared/TreatmentApproachContent"
import { InsuranceContent } from "../shared/InsuranceContent"
import ProfileHeader from "../shared/ProfileHeader"
import ProfileSection from "../shared/ProfileSection"
import { extractUniqueValues } from "../utils/extractUniqueValues"
import { TherapistContent } from "./TherapistContent"
import { PublicProfilePractice } from "../../../domain/Profile/schema"
import { PracticeProfileSubSectionIds } from "../constants"
import { ContactDisplayIdTypes } from "../../../domain/CMS/schema"
import { xUUID } from "../../../routes"
import {
  PracticeTypeDisplayId,
  ProviderTypeDisplayId,
} from "../../../domain/enums/displayId.enum"
import { useCMSReady } from "../../../domain/CMS/useCMSReady"
import {
  getPracticeGroupPolicyBannerData,
  getProviderLiabilityBannerData,
  getPracticeLicenseBannerData,
  getPracticeAgreementBannerData,
} from "../utils/bannerHelpers"
import { practiceAgreementSignatureRequired } from "../../../domain/Practice/helpers"
import { LocationTypeDisplayId } from "../../../domain/Practice/schema"
import { useFeatureFlagEnabled } from "posthog-js/react"
import { FeatureFlag } from "../../../domain/enums/featureFlags"
import { hasVirtualSessionType } from "../utils/hasVirtualSessionType"
import { type BannerType } from "../../shared/components/Banner"
import useContentLayoutContext from "../../layout/useContentLayoutContext"

interface PracticeProfileProps {
  practiceProfileData: PublicProfilePractice | undefined
}

const PracticeProfile: React.FC<PracticeProfileProps> = ({
  practiceProfileData,
}) => {
  const { cms: cmsContent, isReady: isCMSReady } = useCMSReady([
    "publicProfileSlide",
  ])
  const publicProfileText = cmsContent.publicProfileSlide
  const { scrollableContainerRef } = useContentLayoutContext()

  const {
    name,
    contactInfo,
    tags,
    isVisible,
    websiteUrl,
    providersInfo,
    locations,
    id: practiceId,
    status,
    typeId,
    authReleaseRequired,
    liabilityInsurances,
  } = practiceProfileData || {}

  const { emails, phones } = contactInfo || {}

  const primaryEmail =
    emails?.filter((email) => email.isPrimary)[0]?.address || ""

  const { providerInsuranceDisplayIds = [], providerTherapyTypeDisplayIds } =
    extractUniqueValues(providersInfo || [], [
      "providerInsuranceDisplayIds",
      "providerTherapyTypeDisplayIds",
    ])

  const updatedPhoneList =
    phones?.map((phone) => ({
      id: phone.id,
      number: phone.number,
      phoneTypeDisplayId: phone.phoneType as ContactDisplayIdTypes,
      isPrimary: phone.isPrimary,
    })) || []

  const [selectedMarkerId, setSelectedMarkerId] = useState<string>()

  const isNoVirtualLocationEnabled = useFeatureFlagEnabled(
    FeatureFlag.IsNoVirtualLocationEnabled,
  )

  const isPracticeAgreementSignatureRequiredEnabled = useFeatureFlagEnabled(
    FeatureFlag.IsPracticeAgreementSignatureRequired20409Enabled,
  )

  const hasVirtualServices =
    isNoVirtualLocationEnabled &&
    (providersInfo?.some(
      (provider) =>
        provider.isVirtualAttested === true &&
        hasVirtualSessionType(provider.providerSessionTypeDisplayIds),
    ) ??
      false)

  const displayLocations = useMemo(() => {
    if (!locations) return []
    if (isNoVirtualLocationEnabled && !hasVirtualServices) {
      return locations.filter(
        (loc) => loc.locationTypeDisplayId !== LocationTypeDisplayId.VIRTUAL,
      )
    }
    return locations
  }, [locations, isNoVirtualLocationEnabled, hasVirtualServices])

  const providersAcceptingNewPatients =
    (providersInfo?.filter((provider) => provider.isAcceptingNewPatients) ?? [])
      .length > 0
  const providerAcceptingOnWaitlist =
    (providersInfo?.filter((provider) => provider.hasPatientWaitList) ?? [])
      .length > 0

  const numberOfProviders = providersInfo?.length ?? 0
  let acceptingNewPatientsStatus =
    numberOfProviders > 0 ? NewPatientStatusEnum.NOT_ACCEPTING : undefined
  if (providersAcceptingNewPatients) {
    acceptingNewPatientsStatus = NewPatientStatusEnum.ACCEPTING
  } else if (providerAcceptingOnWaitlist) {
    acceptingNewPatientsStatus = NewPatientStatusEnum.ACCEPTING_WITH_WAITLIST
  }

  const providerCareLists = providersInfo?.map(
    (provider) => provider.providerSessionTypeDisplayIds,
  )
  const uniqueProvidedCare = Array.from(new Set(providerCareLists?.flat()))

  /**
   * Calculates banner data for license and liability warnings in the practice profile header.
   * Returns data indicating whether to show banners, their text, and styling.
   */
  const bannerData = useMemo(() => {
    // Early return with empty state if CMS content not ready
    if (!isCMSReady) {
      return {
        showLicenseBanner: false,
        licenseText: "",
        showLiabilityBanner: false,
        liabilityText: "",
        liabilityClickable: false,
        liabilityType: "error" as BannerType,
        showDisclosureBanner: false,
        disclosureText: "",
      }
    }

    const practiceHasGroupPolicy = authReleaseRequired

    // Calculate liability banner data based on practice type
    const liabilityData = practiceHasGroupPolicy
      ? getPracticeGroupPolicyBannerData(
          liabilityInsurances,
          publicProfileText!,
        )
      : getProviderLiabilityBannerData(
          providersInfo,
          publicProfileText?.providerLiabilityNeedsAttentionBannerText ?? "",
        )

    // Calculate license banner data from provider licenses
    const licenseData = getPracticeLicenseBannerData(
      providersInfo,
      publicProfileText?.providerLicenseNeedsAttentionBannerText ?? "",
    )

    const anyProviderMissingDisclosure = !!providersInfo?.some(
      (practiceProvider) =>
        !practiceProvider?.providerDisclosures?.some(
          (disclosure) =>
            disclosure.fileId != null &&
            disclosure.envelopeId != null &&
            disclosure.archivedDateTime == null,
        ) &&
        practiceProvider.providerTypeDisplayId !==
          ProviderTypeDisplayId.MANAGED, // Managed providers don't have or need signed disclosures
    )

    const disclosureData = {
      showDisclosureBanner: anyProviderMissingDisclosure,
      disclosureText: anyProviderMissingDisclosure
        ? publicProfileText?.disclosureNeedsAttentionBannerText || ""
        : "",
    }

    const isSignatureRequired = practiceProfileData
      ? practiceAgreementSignatureRequired(practiceProfileData, {
          skipCaliforniaExclusion:
            !!isPracticeAgreementSignatureRequiredEnabled,
        })
      : false

    const practiceAgreementData = isPracticeAgreementSignatureRequiredEnabled
      ? getPracticeAgreementBannerData(isSignatureRequired, publicProfileText!)
      : { showPracticeAgreementBanner: false, practiceAgreementText: "" }

    return {
      ...licenseData,
      ...liabilityData,
      ...disclosureData,
      ...practiceAgreementData,
    }
  }, [
    isCMSReady,
    authReleaseRequired,
    liabilityInsurances,
    publicProfileText,
    providersInfo,
    practiceProfileData,
    isPracticeAgreementSignatureRequiredEnabled,
  ])

  const profileSections = [
    {
      id: PracticeProfileSubSectionIds.PRACTICE_PROFILE_AT_A_GLANCE_SECTION,
      label: publicProfileText?.atAGlanceSubsectionTitle || "",
      component: AtAGlanceContent,
      props: {
        overview: practiceProfileData?.overview || "",
      },
    },
    {
      id: PracticeProfileSubSectionIds.PRACTICE_PROFILE_LOCATION_SECTION,
      label: publicProfileText?.location || "",
      component: Location,
      props: isNoVirtualLocationEnabled
        ? {
            locations: displayLocations,
            setSelectedMarkerId,
            hasVirtualServices,
          }
        : displayLocations.length > 0
          ? {
              locations: displayLocations,
              setSelectedMarkerId,
            }
          : undefined,
    },
    {
      id: PracticeProfileSubSectionIds.PRACTICE_PROFILE_NEW_PATIENT_STATUS_SECTION,
      label: publicProfileText?.newPatientStatus || "",
      component: NewPatientStatus,
      props: {
        acceptingNewPatientsStatus,
      },
    },
    {
      id: PracticeProfileSubSectionIds.PRACTICE_PROFILE_PROVIDED_CARE_SECTION,
      label: publicProfileText?.providedCare || "",
      component: ProvidedCare,
      props:
        uniqueProvidedCare.length > 0 || numberOfProviders > 0
          ? {
              uniqueProvidedCare,
              numberOfProviders: numberOfProviders,
            }
          : undefined,
    },
    {
      id: PracticeProfileSubSectionIds.PRACTICE_PROFILE_LOCATION_MAP_SECTION,
      label: publicProfileText?.locationMap || "",
      component: LocationMap,
      props:
        displayLocations.length > 0
          ? {
              practiceId: practiceProfileData?.id,
              locations: displayLocations,
              selectedMarkerId,
              setSelectedMarkerId,
            }
          : undefined,
      padX: false,
    },
    {
      id: PracticeProfileSubSectionIds.PRACTICE_PROFILE_SPECIALITIES_SECTION,
      label: publicProfileText?.specialties || "",
      component: SpecialitiesContent,
      props: { providers: providersInfo },
    },
    {
      id: PracticeProfileSubSectionIds.PRACTICE_PROFILE_CLIENT_FOCUS_SECTION,
      label: publicProfileText?.clientFocus || "",
      component: ClientFocusContent,
      props: { providers: providersInfo },
    },
    {
      id: PracticeProfileSubSectionIds.PRACTICE_PROFILE_TREATMENT_APPROACH_SECTION,
      label: publicProfileText?.treatmentApproach || "",
      component: TreatmentApproachContent,
      props:
        providerTherapyTypeDisplayIds.length > 0
          ? { therapyTypes: providerTherapyTypeDisplayIds }
          : {},
    },
    {
      id: PracticeProfileSubSectionIds.PRACTICE_PROFILE_INSURANCE_SECTION,
      label: publicProfileText?.insurance || "",
      component: InsuranceContent,
      props: {
        insuranceDisplayIds: providerInsuranceDisplayIds,
        notAcceptingInsurance: providerInsuranceDisplayIds.length < 1,
      },
    },
    {
      id: PracticeProfileSubSectionIds.PRACTICE_PROFILE_THERAPIST_SECTION,
      label: publicProfileText?.therapists || "",
      component: TherapistContent,
      props:
        providersInfo && providersInfo.length > 0
          ? {
              providers: providersInfo,
              practiceLocations: displayLocations,
              practiceInfo: {
                practiceName: name,
                practiceId: practiceId,
                practiceStatus: status,
                authReleaseRequired: practiceProfileData?.authReleaseRequired,
              },
              practiceLiabilityInsurances: liabilityInsurances,
            }
          : {},
    },
  ]

  return (
    <>
      <ProfileHeader
        isPublicViewable={isVisible || false}
        phoneNumberList={updatedPhoneList}
        primaryEmailAddress={primaryEmail}
        profileId={practiceId ?? xUUID}
        profileName={name || ""}
        profileSections={profileSections}
        profileType="practiceProfile"
        profileTagDisplayIds={tags}
        websiteUrl={websiteUrl}
        practiceStatus={status}
        hasProviders={
          !!providersInfo?.some(
            (provider) => provider.providerStatusDisplayId === "active",
          )
        }
        isManaged={typeId === PracticeTypeDisplayId.MANAGED}
        // Banner-related data for practice level banners/badging
        bannerData={bannerData}
      />
      <div
        className="overflow-y-auto pb-32 scroll-smooth"
        ref={scrollableContainerRef}
      >
        <div className="flex flex-col flex-grow gap-5 mt-5">
          {profileSections?.map((item) => (
            <ProfileSection
              key={item.id}
              id={item.id}
              label={item.label}
              component={item.component}
              sectionProps={item?.props}
              padX={item.padX}
            />
          ))}
        </div>
      </div>
    </>
  )
}

export default PracticeProfile
